import { TicketListing } from "./components/TicketListing";
import { TicketListingShimmer } from "./components/TicketListingShimmer";
import { TicketTableListing } from "./components/TicketTableListing";
import { TicketTableListingShimmer } from "./components/TicketTableListingShimmer";
import { HelpDeskStage, HelpDeskTicket } from "./utils/types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../../redux/store";
import {
  moveTicketBetweenStages,
  updateTicketData,
  clearPendingTicketMove,
} from "../../../../../../redux/slices/helpdeskSlice";
import { SessionStorageUtil } from "../../../../../../utils/storage/storageUtils";
import { ANNOUNCEMENT_BANNER_DISMISSED_KEY } from "../../../../../../utils/constants/announcementBannerInfo";
import { DragDropProvider } from "@dnd-kit/react";
import { useUpdateTicketMutation } from "../../../../../../redux/services/helpdeskApi";
import { customToast } from "../../../../../../utils/custom-toast/custom-toast";
import { getClientDomain } from "../../../../../../utils/functions/client-code";
import { visibleScrollbar } from "../../../../../../utils/functions/get-visible-scrollbar";
import { isHelpdeskResolutionClient } from "../../utils/function";
import ResolutionReasonModal from "./components/ResolutionReasonModal";
import WaitingOnThirdPartyModal from "./components/WaitingOnThirdPartyModal";
import AssigneeRequiredModal from "./components/AssigneeRequiredModal";
import { getHelpdeskLabels } from "../../../../../../utils/constants/mappings";

interface AllTicketsProps {
  helpdeskTicketsData?: HelpDeskStage[];
  isLoading?: boolean;
  isFetching?: boolean;
  selectedViewId?: string;
  systemNameFilter?: string;
}

export const AllTickets = ({
  helpdeskTicketsData,
  isLoading,
  isFetching,
  selectedViewId = "all_tickets",
  systemNameFilter,
}: AllTicketsProps) => {
  const dispatch = useDispatch();
  const isResolutionEnabled = isHelpdeskResolutionClient(getClientDomain());

  const view = useSelector((state: RootState) => state.helpdesk.activeView);
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);

  const [activeSortStageKey, setActiveSortStageKey] = useState<string | null>(
    null
  );

  const [updateTicket] = useUpdateTicketMutation();
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [isSubmittingResolution, setIsSubmittingResolution] = useState(false);
  const [isDraggingTicket, setIsDraggingTicket] = useState(false);
  const [dragFetchStarted, setDragFetchStarted] = useState(false);
  const [pendingClosedMove, setPendingClosedMove] = useState<{
    ticket: HelpDeskTicket;
    fromStageKey: string;
    toStageKey: string;
  } | null>(null);
  const [showThirdPartyModal, setShowThirdPartyModal] = useState(false);
  const [isSubmittingThirdParty, setIsSubmittingThirdParty] = useState(false);
  const [pendingThirdPartyMove, setPendingThirdPartyMove] = useState<{
    ticket: HelpDeskTicket;
    fromStageKey: string;
    toStageKey: string;
  } | null>(null);
  const [showAssigneeRequiredModal, setShowAssigneeRequiredModal] =
    useState(false);

  const isBannerVisible =
    SessionStorageUtil.getItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY) !== "true";

  const handleSortPopperToggle = useCallback((stageKey: string) => {
    setActiveSortStageKey((prev) => (prev === stageKey ? null : stageKey));
  }, []);

  const handleSortPopperClose = useCallback(() => {
    setActiveSortStageKey(null);
  }, []);

  const handleDragStart = useCallback(() => {
    setIsDraggingTicket(true);
  }, []);

  const handleDragEnd = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (event: any) => {
      const { operation, canceled } = event;

      if (canceled) {
        setIsDraggingTicket(false);
        return;
      }

      const active = operation?.source;
      const over = operation?.target;

      if (!over || !active) {
        setIsDraggingTicket(false);
        return;
      }

      const activeData = active?.data as {
        type: string;
        ticketId: number;
        fromStageKey: string;
        ticket: HelpDeskTicket;
      };
      const overData = over?.data as { type: string; stageKey: string };

      if (
        activeData?.type !== "ticket" ||
        overData?.type !== "ticket-stage" ||
        activeData?.fromStageKey === overData?.stageKey
      ) {
        setIsDraggingTicket(false);
        return;
      }

      const ticket = activeData?.ticket;
      const fromStageKey = activeData?.fromStageKey;
      const toStageKey = overData?.stageKey;

      if (toStageKey === "closed" && !ticket?.assignees?.length) {
        setIsDraggingTicket(false);
        setShowAssigneeRequiredModal(true);
        return;
      }

      const needsConfirmation =
        isResolutionEnabled &&
        (toStageKey === "waiting-on-third-party" || toStageKey === "closed");

      const movedTicket = needsConfirmation
        ? { ...ticket, resolution_reason: undefined }
        : ticket;

      dispatch(
        moveTicketBetweenStages({
          ticket: movedTicket,
          fromStageKey,
          toStageKey,
        })
      );

      if (toStageKey === "waiting-on-third-party" && isResolutionEnabled) {
        setPendingThirdPartyMove({ ticket, fromStageKey, toStageKey });
        setShowThirdPartyModal(true);
        return;
      }

      if (toStageKey === "closed" && isResolutionEnabled) {
        setPendingClosedMove({ ticket, fromStageKey, toStageKey });
        setShowResolutionModal(true);
        return;
      }

      try {
        const response = await updateTicket({
          ticketId: String(ticket?.ticket_id),
          stage_key: toStageKey,
        }).unwrap();

        dispatch(
          updateTicketData({
            ticketId: ticket?.ticket_id,
            updatedTicket: response as HelpDeskTicket,
          })
        );

        customToast(`${helpdeskLabels.ticketSingular} stage updated successfully`, "success");
      } catch {
        dispatch(
          moveTicketBetweenStages({
            ticket,
            fromStageKey: toStageKey,
            toStageKey: fromStageKey,
          })
        );
        customToast(`Failed to update ${helpdeskLabels.ticketSingularLower} stage`, "error");
        setIsDraggingTicket(false);
      }
    },
    [dispatch, updateTicket, isResolutionEnabled]
  );

  const handleResolutionSubmit = useCallback(
    async (resolutionReason: string) => {
      if (!pendingClosedMove) return;
      const { ticket, fromStageKey, toStageKey } = pendingClosedMove;
      setIsSubmittingResolution(true);
      try {
        const response = await updateTicket({
          ticketId: String(ticket?.ticket_id),
          stage_key: toStageKey,
          resolution_reason: resolutionReason,
        }).unwrap();

        dispatch(
          updateTicketData({
            ticketId: ticket?.ticket_id,
            updatedTicket: response as HelpDeskTicket,
          })
        );

        customToast(`${helpdeskLabels.ticketSingular} closed successfully`, "success");
      } catch {
        dispatch(
          moveTicketBetweenStages({
            ticket,
            fromStageKey: toStageKey,
            toStageKey: fromStageKey,
          })
        );
        customToast(`Failed to close ${helpdeskLabels.ticketSingularLower}`, "error");
      } finally {
        setIsSubmittingResolution(false);
        setShowResolutionModal(false);
        setPendingClosedMove(null);
      }
    },
    [pendingClosedMove, updateTicket, dispatch]
  );

  const handleResolutionCancel = useCallback(() => {
    if (pendingClosedMove) {
      dispatch(
        moveTicketBetweenStages({
          ticket: pendingClosedMove.ticket,
          fromStageKey: pendingClosedMove.toStageKey,
          toStageKey: pendingClosedMove.fromStageKey,
        })
      );
    }
    setIsDraggingTicket(false);
    setShowResolutionModal(false);
    setPendingClosedMove(null);
  }, [pendingClosedMove, dispatch]);

  const handleThirdPartySubmit = useCallback(
    async (externalVendor: string) => {
      if (!pendingThirdPartyMove) return;
      const { ticket, fromStageKey, toStageKey } = pendingThirdPartyMove;
      setIsSubmittingThirdParty(true);
      try {
        const response = await updateTicket({
          ticketId: String(ticket?.ticket_id),
          stage_key: toStageKey,
          external_vendor: externalVendor,
        }).unwrap();

        dispatch(
          updateTicketData({
            ticketId: ticket?.ticket_id,
            updatedTicket: response as HelpDeskTicket,
          })
        );

        customToast(`${helpdeskLabels.ticketSingular} stage updated successfully`, "success");
      } catch {
        dispatch(
          moveTicketBetweenStages({
            ticket,
            fromStageKey: toStageKey,
            toStageKey: fromStageKey,
          })
        );
        customToast(`Failed to update ${helpdeskLabels.ticketSingularLower} stage`, "error");
      } finally {
        setIsSubmittingThirdParty(false);
        setShowThirdPartyModal(false);
        setPendingThirdPartyMove(null);
      }
    },
    [pendingThirdPartyMove, updateTicket, dispatch]
  );

  const handleAssigneeRequiredClose = useCallback(() => {
    setShowAssigneeRequiredModal(false);
  }, []);

  const handleThirdPartyCancel = useCallback(() => {
    if (pendingThirdPartyMove) {
      dispatch(
        moveTicketBetweenStages({
          ticket: pendingThirdPartyMove.ticket,
          fromStageKey: pendingThirdPartyMove.toStageKey,
          toStageKey: pendingThirdPartyMove.fromStageKey,
        })
      );
    }
    setIsDraggingTicket(false);
    setShowThirdPartyModal(false);
    setPendingThirdPartyMove(null);
  }, [pendingThirdPartyMove, dispatch]);

  useEffect(() => {
    if (!isDraggingTicket) {
      setDragFetchStarted(false);
      const timer = setTimeout(() => {
        dispatch(clearPendingTicketMove());
      }, 100);
      return () => clearTimeout(timer);
    }
    if (isFetching) {
      setDragFetchStarted(true);
    } else if (dragFetchStarted) {
      setIsDraggingTicket(false);
      setDragFetchStarted(false);
    }
  }, [isDraggingTicket, isFetching, dragFetchStarted, dispatch]);

  return (
    <div className="w-full h-full px-4">
      {view === "grid" ? (
        <DragDropProvider
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            className={`${isBannerVisible ? "h-[calc(100dvh-190px)]" : "h-[calc(100dvh-160px)]"} flex gap-3 overflow-x-auto ${visibleScrollbar(
              "[&::-webkit-scrollbar]:h-[8px] [&::-webkit-scrollbar]:w-[8px]"
            )}`}
          >
            {(isDraggingTicket ? isLoading : isFetching)
              ? Array?.from({ length: 4 })?.map((_, i) => (
                  <TicketListingShimmer
                    key={i}
                    cardCount={i % 2 === 0 ? 3 : 2}
                  />
                ))
              : helpdeskTicketsData?.map((stage) => (
                  <TicketListing
                    key={`${selectedViewId}-${systemNameFilter ?? "all"}-${stage?.key}`}
                    stage={stage}
                    selectedViewId={selectedViewId}
                    systemNameFilter={systemNameFilter}
                    showSortPopper={activeSortStageKey === stage?.key}
                    onSortPopperToggle={handleSortPopperToggle}
                    onSortPopperClose={handleSortPopperClose}
                  />
                ))}
          </div>
        </DragDropProvider>
      ) : (
        <div className="flex flex-col gap-2 mt-1 pb-4">
          {isFetching ? (
            <TicketTableListingShimmer />
          ) : (
            <div className="w-full h-full">
              {helpdeskTicketsData?.map((stage) => (
                <TicketTableListing
                  key={`${selectedViewId}-${systemNameFilter ?? "all"}-${stage?.key}`}
                  stage={stage}
                  selectedViewId={selectedViewId}
                  systemNameFilter={systemNameFilter}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <ResolutionReasonModal
        open={showResolutionModal}
        onSubmit={handleResolutionSubmit}
        onCancel={handleResolutionCancel}
        isLoading={isSubmittingResolution}
        initialValue={pendingClosedMove?.ticket?.resolution_reason}
      />

      <WaitingOnThirdPartyModal
        open={showThirdPartyModal}
        onSubmit={handleThirdPartySubmit}
        onCancel={handleThirdPartyCancel}
        isLoading={isSubmittingThirdParty}
        initialValue={pendingThirdPartyMove?.ticket?.external_vendor}
      />

      <AssigneeRequiredModal
        open={showAssigneeRequiredModal}
        onClose={handleAssigneeRequiredClose}
      />
    </div>
  );
};
